import { useEffect, useMemo, useState } from 'react'
import { MapPin, Trash2, X } from 'lucide-react'
import {
  WEEKDAYS,
  parseRRule,
  type Attendee,
  type AttendeeKind,
  type EventDto,
  type MemberDto,
  type OccurrenceDto,
  type RecurrenceFreq,
  type SeriesScope,
  type Weekday,
} from '@familynest/shared'
import { api } from '../../lib/api'
import { toLocalInput, fromLocalInput } from '../../lib/date'
import { mapsUrl } from '../../lib/maps'
import { Avatar, Button, useBackdropClose } from '../ui'
import { useDialogs, type ChooseOption } from '../ui/dialogs'
import { RichText, hasLink } from '../ui/RichText'

/** Ist das ein entfaltetes Vorkommen (statt des Basis-Termins)? */
function asOccurrence(e: EventDto | OccurrenceDto | null): OccurrenceDto | null {
  return e && 'occurrenceStart' in e ? e : null
}

/** Vergleichbare Form der Felder, die nur serienweit änderbar sind. */
function shapeOf(freq: string, interval: number, byWeekday: string[], until: string, allDay: boolean, att: Record<string, string>): string {
  const people = Object.entries(att).filter(([, v]) => v !== 'off').sort(([a], [b]) => a.localeCompare(b))
  return JSON.stringify([freq, freq === 'NONE' ? 1 : interval, [...byWeekday].sort(), until, allDay, people])
}

/** Schlüssel des Vorkommens in der Serie (ADR-087): Original-Start, auch wenn verschoben. */
export function occurrenceKeyOf(o: OccurrenceDto): string {
  return o.originalStart ?? o.occurrenceStart
}

const WD_LABEL: Record<Weekday, string> = { MO: 'Mo', TU: 'Di', WE: 'Mi', TH: 'Do', FR: 'Fr', SA: 'Sa', SU: 'So' }
const FREQ_OPTIONS: Array<{ v: RecurrenceFreq | 'NONE'; label: string }> = [
  { v: 'NONE', label: 'Einmalig' },
  { v: 'DAILY', label: 'Täglich' },
  { v: 'WEEKLY', label: 'Wöchentlich' },
  { v: 'MONTHLY', label: 'Monatlich' },
  { v: 'YEARLY', label: 'Jährlich' },
]
const REMINDER_OPTIONS = [
  { v: null, label: 'Keine' },
  { v: 0, label: 'Zum Zeitpunkt' },
  { v: 10, label: '10 Min vorher' },
  { v: 30, label: '30 Min vorher' },
  { v: 60, label: '1 Std vorher' },
  { v: 1440, label: '1 Tag vorher' },
]

// Teilnahme-Status im Dialog: aus / teilnehmend / nur Info (durchklickbar).
type AttState = 'off' | 'participant' | 'info'

const field =
  'rounded-lg border px-3 py-2 outline-none focus:border-[var(--color-accent)] w-full'
const fieldStyle = {
  backgroundColor: 'var(--color-surface)',
  borderColor: 'var(--color-line)',
  color: 'var(--color-ink)',
} as const

/**
 * Termin anlegen/bearbeiten im einen Familienkalender (ADR-026). Statt Kalender-
 * Auswahl: Personen als teilnehmend / nur Info markieren. „teilnehmend" bekommt
 * die Erinnerung. RBAC spiegelt den Server (Kind nur eigene Termine).
 */
export function EventDialog({
  editing,
  defaultDate,
  exactTime = false,
  members,
  member,
  onClose,
  onSaved,
}: {
  editing: EventDto | OccurrenceDto | null
  defaultDate: Date
  /** true = defaultDate enthält schon die gewünschte Uhrzeit (Klick ins Zeitraster). */
  exactTime?: boolean
  members: MemberDto[]
  member: MemberDto
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const [title, setTitle] = useState('')
  const [allDay, setAllDay] = useState(false)
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [location, setLocation] = useState('')
  const [description, setDescription] = useState('')
  const [freq, setFreq] = useState<RecurrenceFreq | 'NONE'>('NONE')
  const [interval, setInterval] = useState(1)
  const [byWeekday, setByWeekday] = useState<Weekday[]>([])
  const [until, setUntil] = useState('')
  const [reminder, setReminder] = useState<number | null>(null)
  const [attMap, setAttMap] = useState<Record<string, AttState>>({})
  // Bei neuen Terminen folgt das Ende automatisch auf Beginn +1h, bis der Nutzer
  // das Ende selbst anpasst (dann bleibt es unverändert).
  const [endEdited, setEndEdited] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [knownLocations, setKnownLocations] = useState<string[]>([])
  const dialogs = useDialogs()
  const occ = asOccurrence(editing)
  const isSeries = !!editing?.rrule
  // Ausgangszustand von Wiederholung/Teilnehmern/Ganztägig — ändert sich davon
  // etwas, ist „nur dieser Termin" nicht möglich (Ausnahmen betreffen nur
  // Zeit, Titel, Ort, Beschreibung, Erinnerung; ADR-087).
  const initialShape = useMemo(() => {
    if (!editing) return ''
    const r = editing.rrule ? parseRRule(editing.rrule) : null
    return shapeOf(
      r?.freq ?? 'NONE',
      r?.interval ?? 1,
      r?.byWeekday ?? [],
      r?.until ? toLocalInput(new Date(r.until)) : '',
      editing.allDay,
      Object.fromEntries(editing.attendees.map((a) => [a.memberId, a.kind])),
    )
  }, [editing])
  const shape = (): string => shapeOf(freq, interval, byWeekday, until, allDay, attMap)

  useEffect(() => {
    const initAtt: Record<string, AttState> = {}
    if (editing) {
      const o = asOccurrence(editing)
      setTitle(editing.title)
      setAllDay(editing.allDay)
      // Serien: Zeiten DIESES Vorkommens zeigen, nicht den Serienbeginn.
      setStart(toLocalInput(new Date(o ? o.occurrenceStart : editing.startsAt)))
      setEnd(toLocalInput(new Date(o ? o.occurrenceEnd : editing.endsAt)))
      setLocation(editing.location ?? '')
      setDescription(editing.description ?? '')
      setReminder(editing.reminderMinutes)
      const r = editing.rrule ? parseRRule(editing.rrule) : null
      setFreq(r?.freq ?? 'NONE')
      setInterval(r?.interval ?? 1)
      setByWeekday(r?.byWeekday ?? [])
      setUntil(r?.until ? toLocalInput(new Date(r.until)) : '')
      for (const a of editing.attendees) initAtt[a.memberId] = a.kind
    } else {
      const s = new Date(defaultDate)
      if (!exactTime) s.setHours(Math.max(s.getHours(), 9), 0, 0, 0)
      const e = new Date(s)
      e.setHours(s.getHours() + 1)
      setTitle('')
      setAllDay(false)
      setStart(toLocalInput(s))
      setEnd(toLocalInput(e))
      setLocation('')
      setDescription('')
      setFreq('NONE')
      setInterval(1)
      setByWeekday([])
      setUntil('')
      setReminder(null)
      // Ersteller ist standardmäßig teilnehmend (kann im Dialog geändert werden).
      if (member.role !== 'guest') initAtt[member.id] = 'participant'
    }
    setAttMap(initAtt)
    // Beim Bearbeiten das Ende nicht automatisch nachziehen; nur bei neuen Terminen.
    setEndEdited(editing !== null)
    setError(null)
  }, [editing, defaultDate, member, exactTime])


  // Aus einem externen iCal-Abo importierte Termine sind schreibgeschützt.
  const fromSubscription = !!editing?.subscriptionId
  const canWrite = !fromSubscription && (!editing || member.role === 'adult' || editing.createdBy === member.id)
  const canDelete = editing !== null && canWrite

  // Orte der Familie fürs Autocomplete (ADR-090) — nur wer schreiben darf.
  useEffect(() => {
    if (!canWrite) return
    api.eventLocations().then(setKnownLocations).catch(() => setKnownLocations([]))
  }, [canWrite])

  /** Fragt bei Serien, worauf sich die Aktion bezieht. null = abgebrochen. */
  async function askScope(action: 'save' | 'delete'): Promise<SeriesScope | null> {
    if (!isSeries || !occ) return 'all'
    const onlyThisPossible = action === 'delete' || shape() === initialShape
    const options: ChooseOption[] = [
      ...(onlyThisPossible ? [{ value: 'this', label: 'Nur dieser Termin', hint: action === 'delete' ? 'Fällt nur an diesem Tag aus' : undefined }] : []),
      { value: 'following', label: 'Dieser und alle folgenden' },
      { value: 'all', label: 'Alle Termine der Serie', danger: action === 'delete' },
    ]
    const v = await dialogs.choose({
      title: action === 'delete' ? 'Serientermin löschen' : 'Serientermin ändern',
      message: !onlyThisPossible ? 'Wiederholung, Teilnehmer oder „Ganztägig" lassen sich nur für mehrere Termine ändern.' : undefined,
      options,
    })
    return (v as SeriesScope | null) ?? null
  }

  // Beginn ändern: Ende automatisch nachziehen (neuer Termin, solange das Ende
  // nicht manuell angepasst wurde). Ganztägig → gleicher Tag; sonst Beginn +1h.
  function handleStartChange(v: string): void {
    setStart(v)
    if (editing === null && !endEdited) {
      if (allDay) {
        setEnd(v.slice(0, 10) + 'T00:00')
      } else {
        const d = fromLocalInput(v)
        if (!Number.isNaN(d.getTime())) setEnd(toLocalInput(new Date(d.getTime() + 60 * 60 * 1000)))
      }
    }
  }
  function handleEndChange(v: string): void {
    setEnd(v)
    setEndEdited(true)
  }

  // „Ganztägig" umschalten: Uhrzeit aus Beginn/Ende entfernen (auf Tagesgrenze
  // setzen) bzw. beim Deaktivieren eine sinnvolle Uhrzeit zurückgeben.
  function handleAllDayChange(checked: boolean): void {
    setAllDay(checked)
    setStart((s) => s.slice(0, 10) + (checked ? 'T00:00' : 'T09:00'))
    setEnd((e) => e.slice(0, 10) + (checked ? 'T00:00' : 'T10:00'))
  }

  function cycleAttendee(id: string): void {
    setAttMap((prev) => {
      const cur = prev[id] ?? 'off'
      const next: AttState = cur === 'off' ? 'participant' : cur === 'participant' ? 'info' : 'off'
      return { ...prev, [id]: next }
    })
  }

  function toggleWeekday(wd: Weekday): void {
    setByWeekday((prev) => (prev.includes(wd) ? prev.filter((x) => x !== wd) : [...prev, wd]))
  }

  async function save(): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      const recurrence =
        freq === 'NONE'
          ? null
          : {
              freq,
              interval,
              ...(freq === 'WEEKLY' && byWeekday.length ? { byWeekday } : {}),
              ...(until ? { until: fromLocalInput(until).toISOString() } : {}),
            }
      const attendees: Attendee[] = Object.entries(attMap)
        .filter(([, s]) => s !== 'off')
        .map(([memberId, s]) => ({ memberId, kind: s as AttendeeKind }))
      const payload = {
        title: title.trim(),
        startsAt: fromLocalInput(start).toISOString(),
        endsAt: fromLocalInput(end).toISOString(),
        allDay,
        location: location.trim() || null,
        description: description.trim() || null,
        recurrence,
        reminderMinutes: reminder,
        attendees,
      }
      if (!editing) {
        await api.createEvent(payload)
        onSaved()
        return
      }
      const scope = await askScope('save')
      if (!scope) return
      if (scope === 'all' && occ && isSeries) {
        // Gezeigt wird das Vorkommen; „alle" verschiebt den Serienbeginn um
        // dieselbe Differenz (unverändert → Differenz 0 → Ausnahmen bleiben).
        const delta = Date.parse(payload.startsAt) - Date.parse(occ.occurrenceStart)
        const dur = Date.parse(payload.endsAt) - Date.parse(payload.startsAt)
        const base = Date.parse(editing.startsAt) + delta
        await api.updateEvent(editing.id, { ...payload, startsAt: new Date(base).toISOString(), endsAt: new Date(base + dur).toISOString() })
      } else if (scope === 'this' && occ) {
        const { title: t, startsAt, endsAt, location: l, description: d, reminderMinutes } = payload
        await api.updateEvent(editing.id, { scope, occurrenceStart: occurrenceKeyOf(occ), title: t, startsAt, endsAt, location: l, description: d, reminderMinutes })
      } else if (scope === 'following' && occ) {
        await api.updateEvent(editing.id, { ...payload, scope, occurrenceStart: occurrenceKeyOf(occ) })
      } else {
        await api.updateEvent(editing.id, payload)
      }
      onSaved()
    } catch {
      setError('Speichern fehlgeschlagen. Bitte Eingaben prüfen.')
    } finally {
      setBusy(false)
    }
  }

  async function remove(): Promise<void> {
    if (!editing) return
    // Serie: Geltungsbereich wählen (ADR-087); ohne Vorkommen-Bezug wie bisher die
    // ganze Serie mit deutlichem Hinweis. Einzeltermin: normale Bestätigung.
    let scope: SeriesScope = 'all'
    if (isSeries && occ) {
      const s = await askScope('delete')
      if (!s) return
      scope = s
    } else {
      const ok = await dialogs.confirm({
        title: isSeries ? 'Serientermin löschen' : 'Termin löschen',
        message: isSeries
          ? `„${editing.title}" ist ein Serientermin — es wird die GANZE Serie gelöscht. Fortfahren?`
          : `„${editing.title}" wirklich löschen?`,
        confirmLabel: 'Löschen',
        danger: true,
      })
      if (!ok) return
    }
    setBusy(true)
    try {
      await api.deleteEvent(editing.id, occ && scope !== 'all' ? { scope, occurrenceStart: occurrenceKeyOf(occ) } : undefined)
      onSaved()
    } catch {
      setError('Löschen fehlgeschlagen.')
      setBusy(false)
    }
  }

  const backdrop = useBackdropClose(onClose)
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" {...backdrop}>
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-auto rounded-t-2xl border p-5 sm:rounded-2xl"
        style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-ink)' }}>
            {editing ? 'Termin bearbeiten' : 'Neuer Termin'}
          </h2>
          <button onClick={onClose} aria-label="Schließen" className="rounded-lg p-1 hover:bg-black/5">
            <X className="size-5" style={{ color: 'var(--color-muted)' }} />
          </button>
        </div>

        <div className="flex flex-col gap-3">
          {fromSubscription && (
            <p className="rounded-lg px-3 py-2 text-xs" style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-muted)' }}>
              Aus einem abonnierten Kalender (Müll/Feiertage/Ferien) — wird automatisch aktualisiert und kann hier nicht bearbeitet werden.
            </p>
          )}
          <input className={field} style={fieldStyle} placeholder="Titel" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus disabled={!canWrite} />

          <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--color-ink)' }}>
            <input type="checkbox" checked={allDay} onChange={(e) => handleAllDayChange(e.target.checked)} disabled={!canWrite} />
            Ganztägig
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1 text-sm">
              <span style={{ color: 'var(--color-muted)' }}>Beginn</span>
              <input
                type={allDay ? 'date' : 'datetime-local'}
                className={field}
                style={fieldStyle}
                value={allDay ? start.slice(0, 10) : start}
                onChange={(e) => handleStartChange(allDay ? e.target.value + 'T00:00' : e.target.value)}
                disabled={!canWrite}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span style={{ color: 'var(--color-muted)' }}>Ende</span>
              <input
                type={allDay ? 'date' : 'datetime-local'}
                className={field}
                style={fieldStyle}
                value={allDay ? end.slice(0, 10) : end}
                onChange={(e) => handleEndChange(allDay ? e.target.value + 'T00:00' : e.target.value)}
                disabled={!canWrite}
              />
            </label>
          </div>

          <div className="flex items-center gap-2">
            <input className={field} style={fieldStyle} placeholder="Ort (optional)" value={location} onChange={(e) => setLocation(e.target.value)} disabled={!canWrite} list="fn-event-locations" maxLength={200} autoComplete="off" />
            {location.trim() && (
              <a
                href={mapsUrl(location)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex shrink-0 items-center gap-1 rounded-lg border px-2.5 py-2 text-sm"
                style={{ borderColor: 'var(--color-line)', color: 'var(--color-accent)' }}
                title="In Karten öffnen"
              >
                <MapPin className="size-4" /> Karte
              </a>
            )}
          </div>
          <datalist id="fn-event-locations">
            {knownLocations.map((l) => (
              <option key={l} value={l} />
            ))}
          </datalist>
          {canWrite ? (
            <>
              <textarea className={field} style={fieldStyle} placeholder="Beschreibung (optional)" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000} />
              {hasLink(description) && <RichText text={description} className="-mt-1 text-xs" />}
            </>
          ) : (
            description.trim() && (
              <div className="rounded-lg px-3 py-2 text-sm" style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-ink)' }}>
                <RichText text={description} />
              </div>
            )
          )}
          {occ?.isException && (
            <p className="text-xs" style={{ color: 'var(--color-muted)' }}>Dieser Termin wurde einzeln geändert und weicht von der Serie ab.</p>
          )}

          {/* Teilnehmer: durchklicken zwischen aus / teilnehmend / nur Info */}
          <div className="flex flex-col gap-1.5">
            <span className="text-sm" style={{ color: 'var(--color-muted)' }}>Wer ist dabei?</span>
            <div className="flex flex-wrap gap-2">
              {members
                .filter((m) => m.role !== 'guest')
                .map((m) => {
                  const s = attMap[m.id] ?? 'off'
                  const style =
                    s === 'participant'
                      ? { backgroundColor: m.color, color: '#fff', borderColor: m.color }
                      : s === 'info'
                        ? { backgroundColor: m.color + '22', color: 'var(--color-ink)', borderColor: m.color }
                        : { backgroundColor: 'var(--color-surface)', color: 'var(--color-muted)', borderColor: 'var(--color-line)' }
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => canWrite && cycleAttendee(m.id)}
                      disabled={!canWrite}
                      className="flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-sm"
                      style={style}
                      title={s === 'participant' ? 'teilnehmend' : s === 'info' ? 'nur Info' : 'nicht dabei'}
                    >
                      <Avatar name={m.name} color={m.color} size={20} src={m.avatarUrl} />
                      {m.name}
                      {s === 'info' && <span className="text-xs opacity-70">· Info</span>}
                    </button>
                  )
                })}
            </div>
            <span className="text-xs" style={{ color: 'var(--color-muted)' }}>
              Tippen wechselt: teilnehmend → nur Info → aus. Nur Teilnehmende bekommen die Erinnerung.
            </span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1 text-sm">
              <span style={{ color: 'var(--color-muted)' }}>Wiederholung</span>
              <select className={field} style={fieldStyle} value={freq} onChange={(e) => setFreq(e.target.value as RecurrenceFreq | 'NONE')} disabled={!canWrite}>
                {FREQ_OPTIONS.map((f) => (
                  <option key={f.v} value={f.v}>
                    {f.label}
                  </option>
                ))}
              </select>
            </label>
            {freq !== 'NONE' && (
              <label className="flex flex-col gap-1 text-sm">
                <span style={{ color: 'var(--color-muted)' }}>Alle … (Intervall)</span>
                <input type="number" min={1} max={99} className={field} style={fieldStyle} value={interval} onChange={(e) => setInterval(Math.max(1, Number(e.target.value)))} disabled={!canWrite} />
              </label>
            )}
          </div>

          {freq === 'WEEKLY' && (
            <div className="flex flex-wrap gap-1.5">
              {WEEKDAYS.map((wd) => {
                const on = byWeekday.includes(wd)
                return (
                  <button
                    key={wd}
                    type="button"
                    onClick={() => toggleWeekday(wd)}
                    className="rounded-full px-3 py-1 text-sm"
                    style={on ? { backgroundColor: 'var(--color-accent)', color: '#fff' } : { backgroundColor: 'var(--color-surface)', color: 'var(--color-ink)', border: '1px solid var(--color-line)' }}
                  >
                    {WD_LABEL[wd]}
                  </button>
                )
              })}
            </div>
          )}

          {freq !== 'NONE' && (
            <label className="flex flex-col gap-1 text-sm">
              <span style={{ color: 'var(--color-muted)' }}>Endet am (optional)</span>
              <input type="datetime-local" className={field} style={fieldStyle} value={until} onChange={(e) => setUntil(e.target.value)} disabled={!canWrite} />
            </label>
          )}

          <label className="flex flex-col gap-1 text-sm">
            <span style={{ color: 'var(--color-muted)' }}>Erinnerung</span>
            <select
              className={field}
              style={fieldStyle}
              value={reminder === null ? '' : String(reminder)}
              onChange={(e) => setReminder(e.target.value === '' ? null : Number(e.target.value))}
              disabled={!canWrite}
            >
              {REMINDER_OPTIONS.map((r) => (
                <option key={r.label} value={r.v === null ? '' : String(r.v)}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>

          {error && <p className="text-sm" style={{ color: 'var(--color-danger)' }}>{error}</p>}

          <div className="mt-1 flex items-center justify-between">
            {canDelete ? (
              <Button variant="danger" onClick={remove} disabled={busy}>
                <Trash2 className="size-4" /> Löschen
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button variant="ghost" onClick={onClose} disabled={busy}>
                {canWrite ? 'Abbrechen' : 'Schließen'}
              </Button>
              {canWrite && (
                <Button onClick={save} disabled={busy || !title.trim()}>
                  {editing ? 'Speichern' : 'Anlegen'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
